import { type UIEvent, useEffect, useRef } from 'react';

import { type AudienceOptionType } from '@/api/audience';
import { SearchInput } from '@/components/Form';
import { type FilterOperatorMulti, type FilterOperatorValue } from '@/components/InstantSearch';

import { CategoryFilter } from '../CategoryFilter';

// We display a loading option as the last item in the list (if there are more pages to fetch).
// If the user sees this loading option, load the next page. Don't wait until the user scrolls all
// the way to the bottom.
const LOADING_OPTION_OFFSET = 30;

type Props = {
  value?: FilterOperatorMulti;
  options?: AudienceOptionType[];
  selectionMode: 'none' | 'single' | 'multiple';
  loadMore?: () => void;
  hasMore?: boolean;
  isLoading?: boolean;
  isLoadingMore?: boolean;
  error?: Error | null;
  searchText?: string;
  onChange?: (value: FilterOperatorValue) => void;
  onSearch: (search: string) => void;
};

const InfiniteScrollCategoryFilter = ({
  loadMore,
  hasMore,
  isLoading,
  isLoadingMore,
  error,
  options,
  searchText,
  onChange,
  onSearch,
  ...rest
}: Props) => {
  const searchRef = useRef<HTMLInputElement>(null);
  const loading = isLoading || isLoadingMore;

  useEffect(() => {
    searchRef.current?.focus();
  }, [searchRef]);

  const handleScroll = (event: UIEvent<HTMLElement>) => {
    const target = event.target as HTMLElement;

    if (
      loadMore &&
      hasMore &&
      !loading &&
      target.scrollTop + target.offsetHeight >= target.scrollHeight - LOADING_OPTION_OFFSET
    ) {
      loadMore();
    }
  };

  return (
    <CategoryFilter
      {...rest}
      isLoading={isLoading}
      isLoadingMore={isLoadingMore}
      error={error}
      options={options}
      onScroll={handleScroll}
      onChange={onChange}
    >
      <SearchInput ref={searchRef} size="medium" value={searchText} onSearch={onSearch} autoFocus />
    </CategoryFilter>
  );
};

export default InfiniteScrollCategoryFilter;
